import { authService } from '../../services/auth/auth.service.js';
import { userService } from '../../services/user/user.service.js';
import router from '../../router/index'
import { safeNavigate } from '../../router/navigation-guard'
import authStorageService from '../../services/auth/auth-storage.service.js';

const user = authStorageService.getUser();
export const state = user
    ? { status: { loggeduser: true }, user }
    : { status: {}, user: null };

export const actions = {
    /**
     * Step 1: Login - Request OTP
     * After successful credentials validation, backend sends OTP to email
     * and returns masked email. User is then redirected to OTP verification screen.
     */
    login({ dispatch, commit }, { email, password, organizationName }) {
        commit('loginRequest', { email });
        return authService.login(email, password, organizationName)
            .then(
                result => { 
                    if (result.status == "SUCCESS") {
                        // Store pending auth data for OTP verification
                        authStorageService.setPendingAuth({
                            username: email,
                            organizationName: organizationName,
                            maskedEmail: result.body?.email || ''
                        });

                        commit('otpRequired', result.body?.email || '');
                        
                        // Navigate to OTP verification screen
                        setTimeout(() => {
                            safeNavigate(router, '/login/verify-otp');
                        }, 100);
                        
                        return { success: true, requiresOtp: true };
                    } else {
                        commit('loginFailure', result.message || 'Login failed');
                        dispatch('notification/error', result.message || 'Login failed', { root: true });
                        return { success: false, requiresOtp: false };
                    }
                },
                error => {
                    commit('loginFailure', error);
                    dispatch('notification/error', error, { root: true });
                    return { success: false, requiresOtp: false };
                }
            );
    },

    /**
     * Step 2: Verify OTP - Complete Authentication
     * Validates OTP and returns full auth data (JWT, user profile, etc.)
     */
    verifyOtp({ dispatch, commit }, { otp }) {
        const pendingAuth = authStorageService.getPendingAuth();
        
        if (!pendingAuth || !pendingAuth.username || !pendingAuth.organizationName) {
            commit('loginFailure', 'No pending verification found. Please login again.');
            dispatch('notification/error', 'No pending verification found. Please login again.', { root: true });
            return Promise.resolve({ success: false });
        }

        commit('verifyingOtp');
        
        return authService.verifyOtp(pendingAuth.username, pendingAuth.organizationName, otp)
            .then(
                result => {
                    if (result.status == "SUCCESS") {
                        // Clear pending auth
                        authStorageService.clearPendingAuth();
                        
                        if (result.body.user.userType.trim() == "staff") {
                            // Use the auth storage service to set all auth data
                            authStorageService.setAuthData({
                                token: result.body.user.token,
                                user: result.body.user,
                                businessConditions: result.body.businessConditions,
                                staffProfile: result.body.staffProfile,
                                roles: result.body.roles,
                                organisation: result.body.organisation
                            });

                            const businessConditions = {};
                            result?.body?.organisation?.services?.forEach(service => {
                                const serviceObj = {
                                    id: service.id,
                                    serviceProviders: []
                                };

                                service.serviceProviders?.forEach(provider => {
                                    serviceObj.serviceProviders.push(provider);
                                });

                                businessConditions[service.serviceType] = serviceObj;
                            });
                            authStorageService.setBusinessConditions(businessConditions);

                            commit('loginSuccess', result.message);
                            
                            // Navigate based on user type
                            setTimeout(() => {
                                const userType = result.body.user.userType.trim();
                                switch (userType) {
                                    case "branchStaff": 
                                        safeNavigate(router, '/teller-dashboard'); 
                                        break;
                                    case "branchAdmin": 
                                    case "hqStaff": 
                                    case "superAdminDefault":
                                    default:
                                        safeNavigate(router, '/'); 
                                        break;
                                }
                            }, 100);
                            return { success: true };
                        } else {
                            commit('loginFailure', 'You are not authorised to access this platform');
                            dispatch('notification/error', 'You are not authorised to access this platform', { root: true });
                            return { success: false };
                        }
                    } else {
                        commit('otpVerificationFailed', result.message || 'Verification failed');
                        dispatch('notification/error', result.message || 'Verification failed', { root: true });
                        return { success: false, message: result.message };
                    }
                },
                error => {
                    commit('otpVerificationFailed', error);
                    dispatch('notification/error', error, { root: true });
                    return { success: false, message: error };
                }
            );
    },

    /**
     * Resend OTP
     * Requests a new OTP code to be sent to the user's email
     */
    resendOtp({ dispatch, commit }) {
        const pendingAuth = authStorageService.getPendingAuth();
        
        if (!pendingAuth || !pendingAuth.username || !pendingAuth.organizationName) {
            dispatch('notification/error', 'No pending verification found. Please login again.', { root: true });
            return Promise.resolve({ success: false });
        }

        commit('resendingOtp');
        
        return authService.resendOtp(pendingAuth.username, pendingAuth.organizationName)
            .then(
                result => {
                    if (result.status == "SUCCESS") {
                        // Update pending auth with new timestamp and masked email
                        authStorageService.setPendingAuth({
                            ...pendingAuth,
                            maskedEmail: result.body?.email || pendingAuth.maskedEmail,
                            timestamp: Date.now()
                        });
                        
                        commit('otpResent', result.body?.email || pendingAuth.maskedEmail);
                        dispatch('notification/success', 'Verification code resent to your email', { root: true });
                        return { success: true };
                    } else {
                        commit('otpResendFailed', result.message || 'Failed to resend code');
                        dispatch('notification/error', result.message || 'Failed to resend code', { root: true });
                        return { success: false, message: result.message };
                    }
                },
                error => {
                    commit('otpResendFailed', error);
                    dispatch('notification/error', error, { root: true });
                    return { success: false, message: error };
                }
            );
    },

    /**
     * Cancel OTP verification and go back to login
     */
    cancelOtpVerification({ commit }) {
        authStorageService.clearPendingAuth();
        commit('otpCancelled');
        safeNavigate(router, '/login');
    },

    // register the user
    registeruser({ dispatch, commit }, user) {
        commit('registerRequest', user);
        return userService.register(user)
            .then(
                response => {
                    if (response.status == "SUCCESS") {
                        commit('registerSuccess', response.body);
                        dispatch('notification/success', response.message, { root: true });
                        return response;
                    } else {
                        commit('registerFailure', response.message);
                        dispatch('notification/error', response.message, { root: true });
                        return response;
                    }
                },
                error => {
                    commit('registerFailure', error);
                    dispatch('notification/error', error, { root: true });
                }
            );
    },

    // Logout the user
    async logout({ commit }) {
        // Clear session storage and httpOnly cookie
        await authStorageService.clearAuthDataAsync();
        commit('logout');
    }
};

export const mutations = {
    loginRequest(state, user) {
        state.status = { loggingIn: true };
        state.user = user;
    },
    loginSuccess(state, user) {
        state.status = { loggeduser: true };
        state.user = user;
    },
    loginFailure(state) {
        state.status = {};
        state.user = null;
    },
    // 2FA OTP mutations
    otpRequired(state, maskedEmail) {
        state.status = { otpRequired: true, maskedEmail };
        state.user = null;
    },
    verifyingOtp(state) {
        state.status = { ...state.status, verifyingOtp: true };
    },
    otpVerificationFailed(state, error) {
        state.status = { ...state.status, verifyingOtp: false, otpError: error };
    },
    resendingOtp(state) {
        state.status = { ...state.status, resendingOtp: true };
    },
    otpResent(state, maskedEmail) {
        state.status = { ...state.status, resendingOtp: false, maskedEmail };
    },
    otpResendFailed(state) {
        state.status = { ...state.status, resendingOtp: false };
    },
    otpCancelled(state) {
        state.status = {};
        state.user = null;
    },
    logout(state) {
        state.status = {};
        state.user = null;
    },
    registerRequest(state) {
        state.status = { registering: true };
    },
    registerSuccess(state) {
        state.status = {};
    },
    registerFailure(state) {
        state.status = {};
    }
};

